import { useState } from 'react';
import { supabase } from '../supabaseClient';
import { useAuth } from '../context/AuthContext';

// Maps quiz pace answer → profile pace_range value
const PACE_MAP: Record<string, string> = {
  Easy: '10:00–12:00',
  Moderate: '8:00–9:00',
  Fast: '7:00–8:00',
  Mixed: '8:00–9:00',
};

const VIBE_OPTIONS = [
  'Social',
  'Beginner-Friendly',
  'Fast',
  'Competitive',
  'Long Run',
  'Trail',
  'Workout',
  'Recovery',
  'Women-Led',
  'Dog-Friendly',
];

type Step = 1 | 2 | 3;

const EXPERIENCE_LEVELS = [
  { value: 'Beginner', hint: 'Just getting started' },
  { value: 'Intermediate', hint: 'Running regularly' },
  { value: 'Advanced', hint: 'Training hard' },
  { value: 'Elite', hint: 'Competitive racer' },
];

type Answers = {
  neighborhood: string;
  pace: string;
  experience: string;
  vibes: string[];
};

export default function OnboardingQuiz({ onDone }: { onDone: () => void }) {
  const { user, refreshProfile } = useAuth();
  const [step, setStep] = useState<Step>(1);
  const [answers, setAnswers] = useState<Answers>({ neighborhood: '', pace: '', experience: '', vibes: [] });
  const [saving, setSaving] = useState(false);

  function toggleVibe(v: string) {
    setAnswers((prev) => ({
      ...prev,
      vibes: prev.vibes.includes(v) ? prev.vibes.filter((x) => x !== v) : [...prev.vibes, v],
    }));
  }

  async function finish() {
    if (!user) { onDone(); return; }
    setSaving(true);
    await supabase.from('profiles').upsert({
      id: user.id,
      neighborhood: answers.neighborhood.trim() || null,
      pace_range: answers.pace ? PACE_MAP[answers.pace] ?? null : null,
      experience_level: answers.experience || null,
      goals: answers.vibes.length > 0 ? answers.vibes : null,
    });
    await refreshProfile();
    setSaving(false);
    onDone();
  }

  const totalSteps = 3;
  const progressPct = Math.round((step / totalSteps) * 100);

  return (
    <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/50 px-4 backdrop-blur-sm">
      <div className="relative w-full max-w-md overflow-hidden rounded-[2rem] bg-white shadow-2xl">
        {/* Progress bar */}
        <div className="h-1 w-full bg-black/8">
          <div
            className="h-full bg-black transition-all duration-500"
            style={{ width: `${progressPct}%` }}
          />
        </div>

        <div className="px-8 pb-8 pt-7">
          {/* Header */}
          <div className="flex items-start justify-between">
            <div>
              <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-black/35">
                Step {step} of {totalSteps}
              </p>
            </div>
            <button
              onClick={onDone}
              className="text-xs text-black/30 transition hover:text-black/60"
            >
              Skip all
            </button>
          </div>

          {/* Step 1 — Neighbourhood */}
          {step === 1 && (
            <div className="mt-5">
              <h2 className="text-2xl font-bold tracking-tight text-black">
                Where are you based?
              </h2>
              <p className="mt-1.5 text-sm text-black/50">
                We'll show you clubs close to home.
              </p>
              <input
                type="text"
                autoFocus
                value={answers.neighborhood}
                onChange={(e) => setAnswers((p) => ({ ...p, neighborhood: e.target.value }))}
                placeholder="Neighbourhood or zip code, e.g. North Park, 92104"
                className="mt-6 w-full rounded-2xl border border-black/10 bg-[#fafafa] px-4 py-3.5 text-sm outline-none transition focus:border-black"
                onKeyDown={(e) => { if (e.key === 'Enter') setStep(2); }}
              />
            </div>
          )}

          {/* Step 2 — Pace + Experience */}
          {step === 2 && (
            <div className="mt-5">
              <h2 className="text-2xl font-bold tracking-tight text-black">
                How do you run?
              </h2>
              <p className="mt-1.5 text-sm text-black/50">
                Pick what feels most like you.
              </p>

              <p className="mt-5 text-xs font-semibold uppercase tracking-[0.14em] text-black/40">Pace</p>
              <div className="mt-2 grid grid-cols-2 gap-3">
                {(['Easy', 'Moderate', 'Fast', 'Mixed'] as const).map((p) => (
                  <button
                    key={p}
                    type="button"
                    onClick={() => setAnswers((prev) => ({ ...prev, pace: p }))}
                    className={`rounded-2xl border p-3 text-left transition ${
                      answers.pace === p
                        ? 'border-black bg-black text-white'
                        : 'border-black/10 bg-[#fafafa] text-black hover:border-black'
                    }`}
                  >
                    <p className="font-semibold text-sm">{p}</p>
                    <p className={`mt-0.5 text-xs ${answers.pace === p ? 'text-white/70' : 'text-black/40'}`}>
                      {p === 'Easy' && '10+ min/mile'}
                      {p === 'Moderate' && '8–10 min/mile'}
                      {p === 'Fast' && 'Under 8 min/mile'}
                      {p === 'Mixed' && 'Depends on the day'}
                    </p>
                  </button>
                ))}
              </div>

              <p className="mt-5 text-xs font-semibold uppercase tracking-[0.14em] text-black/40">Experience</p>
              <div className="mt-2 grid grid-cols-2 gap-3">
                {EXPERIENCE_LEVELS.map(({ value, hint }) => (
                  <button
                    key={value}
                    type="button"
                    onClick={() => setAnswers((prev) => ({ ...prev, experience: value }))}
                    className={`rounded-2xl border p-3 text-left transition ${
                      answers.experience === value
                        ? 'border-black bg-black text-white'
                        : 'border-black/10 bg-[#fafafa] text-black hover:border-black'
                    }`}
                  >
                    <p className="font-semibold text-sm">{value}</p>
                    <p className={`mt-0.5 text-xs ${answers.experience === value ? 'text-white/70' : 'text-black/40'}`}>
                      {hint}
                    </p>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Step 3 — Vibes / Goals */}
          {step === 3 && (
            <div className="mt-5">
              <h2 className="text-2xl font-bold tracking-tight text-black">
                What matters to you?
              </h2>
              <p className="mt-1.5 text-sm text-black/50">
                Pick all that apply — we'll find clubs that match.
              </p>
              <div className="mt-6 flex flex-wrap gap-2">
                {VIBE_OPTIONS.map((v) => (
                  <button
                    key={v}
                    type="button"
                    onClick={() => toggleVibe(v)}
                    className={`rounded-full border px-3.5 py-2 text-sm font-medium transition ${
                      answers.vibes.includes(v)
                        ? 'border-black bg-black text-white'
                        : 'border-black/10 bg-[#fafafa] text-black/70 hover:border-black hover:text-black'
                    }`}
                  >
                    {v}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Navigation */}
          <div className="mt-8 flex items-center justify-between">
            {step > 1 ? (
              <button
                type="button"
                onClick={() => setStep((s) => (s - 1) as Step)}
                className="text-sm text-black/40 transition hover:text-black"
              >
                ← Back
              </button>
            ) : (
              <span />
            )}

            {step < 3 ? (
              <button
                type="button"
                onClick={() => setStep((s) => (s + 1) as Step)}
                className="rounded-full bg-black px-6 py-2.5 text-sm font-semibold text-white transition hover:bg-black/80"
              >
                Next
              </button>
            ) : (
              <button
                type="button"
                onClick={finish}
                disabled={saving}
                className="rounded-full bg-black px-6 py-2.5 text-sm font-semibold text-white transition hover:bg-black/80 disabled:opacity-50"
              >
                {saving ? 'Saving…' : "Let's go"}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
